import { Alert, type AlertSeverity, Column, Row, Typography } from '@tempmee/react';
import {
  SolidCheckCircleIcon,
  SolidExclamationCircleIcon,
  SolidExclamationWarningAlertIcon,
} from '@tempmee/react-icons';
import { type ComponentType } from 'react';
import { type ILikelyToFillIndicator, type TLikelyToFillTone } from './likelyToFillBand';

interface ILikelyToFillLabelProps {
  indicator: ILikelyToFillIndicator;
}

interface IToneStyle {
  Icon: ComponentType<{ size?: number; color?: string; className?: string }>;
  textClass: string;
}

// Same icons and token classes as the DS `RateSelection` indicator, so the dropdown and the
// stepper read the same.
const TONE_STYLE: Record<TLikelyToFillTone, IToneStyle> = {
  positive: { Icon: SolidCheckCircleIcon, textClass: 'text-text-success' },
  neutral: { Icon: SolidExclamationWarningAlertIcon, textClass: 'text-text-secondary' },
  warning: { Icon: SolidExclamationWarningAlertIcon, textClass: 'text-warning-dark' },
  negative: { Icon: SolidExclamationCircleIcon, textClass: 'text-text-error' },
};

const ALERT_SEVERITY: Partial<Record<TLikelyToFillTone, AlertSeverity>> = {
  warning: 'warning',
  negative: 'error',
};

/** The GOTU-13065 Likely to Fill line under the legacy rate dropdown. */
export const LikelyToFillLabel = ({ indicator }: ILikelyToFillLabelProps) => {
  const { Icon, textClass } = TONE_STYLE[indicator.tone];
  const severity = ALERT_SEVERITY[indicator.tone];

  return (
    <Column gap={2} pt={3}>
      <Row
        align="center"
        gap={1}
        pl={5}
        className={textClass}
        data-testid="likely-to-fill-label"
        data-cy="likely-to-fill-label"
        data-tone={indicator.tone}
      >
        <Icon size={20} color="currentColor" className="shrink-0" />
        <Typography variant="body1" as="span" bold className="text-inherit">
          {indicator.label}
        </Typography>
      </Row>
      {indicator.message && severity && (
        <Alert
          variant="filled"
          severity={severity}
          icon={false}
          description={indicator.message}
          data-testid="likely-to-fill-alert"
          data-cy="likely-to-fill-alert"
        />
      )}
    </Column>
  );
};
